import { Account, TransactionCategory, DateFormat } from "../../../../types";

type TransactionType = "expense" | "income" | "transfer";

/**
 * Utility functions for mobile transaction form
 * @param dateFormat - Workspace date format (MM/DD/YYYY or DD/MM/YYYY); date field display respects this
 */
export const useMobileFormUtils = (dateFormat: DateFormat = "MM/DD/YYYY") => {
  // Format currency helper function
  const formatCurrency = (amount: number, currency: string = "USD"): string => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format date to YYYY-MM-DD in local time (not UTC) to avoid timezone issues
  const formatDateLocal = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Format date for display in date field (respects workspace date format)
  const formatDateForDisplay = (dateString: string): string => {
    if (!dateString) return "";
    const [year, month, day] = dateString.split("-");
    if (dateFormat === "DD/MM/YYYY") {
      return `${day}/${month}/${year}`;
    }
    return `${month}/${day}/${year}`;
  };

  // Format time to HH:mm in local time
  const formatTimeLocal = (date: Date): string => {
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${hours}:${minutes}`;
  };

  // Get current time in HH:mm format
  const getCurrentTime = (): string => {
    return formatTimeLocal(new Date());
  };

  // Combine date (YYYY-MM-DD) and time (HH:mm) into ISO string for API
  // If time is empty, use midnight (00:00:00) to indicate time was not set
  const combineDateAndTime = (date: string, time: string): string => {
    const timeToUse = time || "00:00";
    const dateTime = new Date(`${date}T${timeToUse}`);
    return dateTime.toISOString();
  };

  return {
    formatCurrency,
    formatDateLocal,
    formatDateForDisplay,
    formatTimeLocal,
    getCurrentTime,
    combineDateAndTime,
  };
};

/**
 * Get filtered accounts for selection based on transaction type and focused field
 */
export const getFilteredAccounts = (
  accounts: Account[],
  transactionType: TransactionType,
  focusedField: string | null,
  fromAccountId?: string
): Account[] => {
  if (transactionType === "transfer") {
    if (focusedField === "toAccount" && fromAccountId) {
      return accounts.filter(acc => acc.id !== fromAccountId);
    }
  }
  return accounts;
};

/**
 * Get current categories based on transaction type
 */
export const getCurrentCategories = (
  transactionType: TransactionType,
  expenseCategories: TransactionCategory[],
  incomeCategories: TransactionCategory[]
): TransactionCategory[] => {
  return transactionType === "expense" ? expenseCategories : incomeCategories;
};
